// Brand configuration from portal API
export interface BrandConfig {
  id: string;
  name: string;
  subdomain: string;
  custom_domain: string | null;
  logo_url: string | null;
  favicon_url: string | null;
  primary_color: string | null;
  header_bg_color: string | null;
  header_text_color: string | null;
  hero_bg_color: string | null;
  hero_image_url: string | null;
  hero_title: string | null;
  hero_subtitle: string | null;
  font_family: string | null;
  theme_mode: 'light' | 'dark' | 'system';
  contact_email: string | null;
  header_link_text: string | null;
  header_link_url: string | null;
  default_locale: string;
  supported_locales: string[];
}

// Locale information
export interface LocaleInfo {
  code: string;
  name: string;
  native_name: string;
  direction: 'ltr' | 'rtl';
}

// Supported locales for the portal
export const SUPPORTED_LOCALES: LocaleInfo[] = [
  { code: 'en', name: 'English', native_name: 'English', direction: 'ltr' },
  { code: 'es', name: 'Spanish', native_name: 'Español', direction: 'ltr' },
  { code: 'fr', name: 'French', native_name: 'Français', direction: 'ltr' },
  { code: 'de', name: 'German', native_name: 'Deutsch', direction: 'ltr' },
  { code: 'it', name: 'Italian', native_name: 'Italiano', direction: 'ltr' },
  { code: 'pt', name: 'Portuguese', native_name: 'Português', direction: 'ltr' },
  { code: 'nl', name: 'Dutch', native_name: 'Nederlands', direction: 'ltr' },
  { code: 'pl', name: 'Polish', native_name: 'Polski', direction: 'ltr' },
  { code: 'ru', name: 'Russian', native_name: 'Русский', direction: 'ltr' },
  { code: 'ja', name: 'Japanese', native_name: '日本語', direction: 'ltr' },
  { code: 'ko', name: 'Korean', native_name: '한국어', direction: 'ltr' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', native_name: '简体中文', direction: 'ltr' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', native_name: '繁體中文', direction: 'ltr' },
  { code: 'ar', name: 'Arabic', native_name: 'العربية', direction: 'rtl' },
  { code: 'he', name: 'Hebrew', native_name: 'עברית', direction: 'rtl' },
  { code: 'th', name: 'Thai', native_name: 'ไทย', direction: 'ltr' },
  { code: 'vi', name: 'Vietnamese', native_name: 'Tiếng Việt', direction: 'ltr' },
  { code: 'tr', name: 'Turkish', native_name: 'Türkçe', direction: 'ltr' },
  { code: 'uk', name: 'Ukrainian', native_name: 'Українська', direction: 'ltr' },
  { code: 'cs', name: 'Czech', native_name: 'Čeština', direction: 'ltr' },
  { code: 'sv', name: 'Swedish', native_name: 'Svenska', direction: 'ltr' },
  { code: 'da', name: 'Danish', native_name: 'Dansk', direction: 'ltr' },
  { code: 'fi', name: 'Finnish', native_name: 'Suomi', direction: 'ltr' },
  { code: 'no', name: 'Norwegian', native_name: 'Norsk', direction: 'ltr' },
];

export function getLocaleInfo(code: string): LocaleInfo | undefined {
  return SUPPORTED_LOCALES.find((l) => l.code === code);
}

// Category
export interface Category {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  position: number;
  section_count?: number;
  article_count?: number;
}

// Section
export interface Section {
  id: string;
  category_id: string;
  slug: string;
  name: string;
  description: string | null;
  position: number;
  article_count?: number;
  // Populated in detail views
  category?: Category;
}

// Article (list item)
export interface ArticleListItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  updated_at: string;
  visibility: 'public' | 'signed_in' | 'internal' | 'segment';
}

// Article (full detail)
export interface Article {
  id: string;
  section_id: string;
  slug: string;
  title: string;
  body: Record<string, unknown>;
  body_plain: string;
  visibility: 'public' | 'signed_in' | 'internal' | 'segment';
  locale: string;
  parent_article_id?: string | null;
  published_at: string | null;
  updated_at: string;
  // Populated
  section?: Section;
  category?: Category;
}

// Related article
export interface RelatedArticle {
  id: string;
  slug: string;
  title: string;
}

// Search result
export interface SearchResult {
  id: string;
  slug: string;
  title: string;
  snippet: string;
  matched_chunk_text?: string;
  category_name: string;
  section_name: string;
  category_slug: string;
  section_slug: string;
  score?: number;
  source?: 'keyword' | 'semantic' | 'both';
}

// Portal session (viewer)
export interface PortalViewer {
  email: string;
  session_id: string;
}

// Feedback
export interface FeedbackPayload {
  article_id: string;
  helpful: boolean;
  comment?: string;
}

// Deflection tracking
export interface StartDeflectionPayload {
  session_id: string;
  search_query?: string;
  form_subject?: string;
  form_body?: string;
  suggested_article_ids?: string[];
}

export interface StartDeflectionResponse {
  deflection_id: string;
}

export interface UpdateDeflectionPayload {
  deflection_id: string;
  clicked_article_id?: string;
  deflected?: boolean;
}

// =====================================================
// My Requests (Ticket Portal)
// =====================================================

export type TicketStatusCategory = 'open' | 'pending' | 'on_hold' | 'solved' | 'closed';
export type TicketAccessType = 'requester' | 'ccd' | 'organization';
export type RequestScope = 'my_requests' | 'ccd' | 'organization';

export interface PortalTicket {
  id: string;
  subject: string;
  status: string;
  status_category: TicketStatusCategory;
  created_at: string;
  last_updated_at: string;
  last_message_text: string | null;
  message_count: number;
}

export interface PortalTicketDetail {
  id: string;
  subject: string;
  status: string;
  status_category: TicketStatusCategory;
  requester_email: string;
  requester_name: string;
  created_at: string;
  last_updated_at: string;
  closed_at: string | null;
  message_count: number;
  type: string;
  priority: string;
  /** 'email' or 'chat'. The help center replies only on email requests; a chat is answered in the chat. */
  channel: string;
}

export interface PortalCustomField {
  field_id: string;
  title: string;
  field_key: string;
  field_type: string;
  description: string | null;
  editable_by_end_users: boolean;
  options: any;
  value: string | null;
}

export interface PortalMessage {
  id: string;
  sender_type: string;
  sender_name: string | null;
  sender_email: string | null;
  body: string | null;
  body_plain: string | null;
  attachments: any;
  sent_at: string;
}

export interface PortalTicketDetailResponse {
  ticket: PortalTicketDetail;
  customFields: PortalCustomField[];
  accessType: TicketAccessType;
}

export interface PortalRequestsListResponse {
  data: PortalTicket[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface PortalReplyPayload {
  message: string;
  /** One per reply, resent unchanged on a retry. */
  idempotencyKey: string;
}

export interface PortalUpdateRequestPayload {
  status?: 'solved';
  customFields?: { fieldId: string; value: string }[];
}

// Request form
export interface SubmitRequestPayload {
  subject: string;
  message: string;
  email?: string;
  name?: string;
  deflection_id?: string;
  /** One per submission, resent unchanged on a retry. */
  idempotencyKey: string;
}

export interface SubmitRequestResponse {
  ticket_id: string;
}
